// The portal talks only to Outlevo's own API on the same host (F18 FR1: all data via the BFF and /v1).

export class ApiError extends Error {
  constructor(readonly code: string, message: string, readonly status: number, readonly fix?: string, readonly details?: Record<string, unknown>) {
    super(message);
  }
}

type TokenSource = () => Promise<string | null>;
let tokenSource: TokenSource = async () => null;
// F25 support window: the session id survives the reload into the customer app (sessionStorage: this tab only)
const SUPPORT_KEY = 'olv_support_session';
let supportSession: string | null = (() => { try { return sessionStorage.getItem(SUPPORT_KEY); } catch { return null; } })();
export const setTokenSource = (t: TokenSource) => { tokenSource = t; };
/** the session token, for callers that send their own request (the journey tracker's keepalive batch) */
export const currentToken = () => tokenSource();
export const setSupportSession = (id: string | null) => {
  supportSession = id;
  try { if (id) sessionStorage.setItem(SUPPORT_KEY, id); else sessionStorage.removeItem(SUPPORT_KEY); } catch { /* storage blocked: memory only */ }
};
export const getSupportSession = () => supportSession;
/** The customer-data routes a support session reads through (the server refuses every write on them). */
const supportScoped = (path: string) => path.startsWith('/v1/') || path.startsWith('/portal/');

const newKey = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

/**
 * The journey tracker's view of every call (portal/src/lib/track.ts sets it): method, path, the body sent, and the
 * error when it failed. Used for the money steps (wall seen, plan chosen, checkout started); it never throws.
 */
type CallObserver = (c: { method: string; path: string; body?: unknown; error?: ApiError }) => void;
let observer: CallObserver | null = null;
export const observeCalls = (o: CallObserver | null) => { observer = o; };
const seen = (c: Parameters<CallObserver>[0]) => { try { observer?.(c); } catch { /* tracking never breaks a call */ } };

export async function api<T = any>(path: string, init: { method?: string; body?: unknown; raw?: boolean } = {}): Promise<T> {
  const token = await tokenSource();
  const method = init.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json' };
  if (token) headers.authorization = `Bearer ${token}`;
  if (init.body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET') headers['idempotency-key'] = newKey();
  if (supportSession && supportScoped(path)) headers['x-outlevo-support-session'] = supportSession;
  const res = await fetch(path, { method, headers, body: init.body === undefined ? undefined : JSON.stringify(init.body) }).catch((e: unknown) => {
    // a call that never got an answer (network) is still a press the funnel should see
    seen({ method, path, body: init.body, error: new ApiError('NETWORK', (e as Error)?.message ?? 'Network error', 0) });
    throw e;
  });
  if (init.raw) {
    if (!res.ok) throw new ApiError('HTTP', `HTTP ${res.status}`, res.status);
    return (await res.text()) as T;
  }
  const text = await res.text();
  const json = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const e = json?.error ?? {};
    const err = new ApiError(e.code ?? 'HTTP', e.message ?? `HTTP ${res.status}`, res.status, e.fix, e.details);
    seen({ method, path, body: init.body, error: err });
    throw err;
  }
  seen({ method, path, body: init.body });
  return json as T;
}

/** Downloads a CSV through the API (the browser never sees a key; the session token goes in the header). */
export async function download(path: string, filename: string) {
  const text = await api<string>(path, { raw: true });
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
  const a = Object.assign(document.createElement('a'), { href: url, download: filename });
  a.click();
  URL.revokeObjectURL(url);
}

/** Downloads a file of any type (an XLSX is binary) through the API, with the session token in the header. */
export async function downloadBlob(path: string, fallbackName: string) {
  const token = await tokenSource();
  const headers: Record<string, string> = {};
  if (token) headers.authorization = `Bearer ${token}`;
  const res = await fetch(path, { headers });
  if (!res.ok) {
    const e = (await res.json().catch(() => null))?.error ?? {};
    throw new ApiError(e.code ?? 'HTTP', e.message ?? `HTTP ${res.status}`, res.status, e.fix, e.details);
  }
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? fallbackName;
  const url = URL.createObjectURL(await res.blob());
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * A POST that answers with server-sent events (the assistant): each `event:`/`data:` block is passed to onEvent.
 * A JSON answer (no stream) is returned as is.
 */
export async function apiStream(path: string, body: unknown, onEvent: (event: string, data: any) => void, signal?: AbortSignal): Promise<any> {
  const token = await tokenSource();
  const headers: Record<string, string> = { accept: 'text/event-stream, application/json', 'content-type': 'application/json', 'idempotency-key': newKey() };
  if (token) headers.authorization = `Bearer ${token}`;
  if (supportSession && supportScoped(path)) headers['x-outlevo-support-session'] = supportSession;
  const res = await fetch(path, { method: 'POST', headers, body: JSON.stringify(body), signal });
  if (!res.ok) {
    const json = await res.json().catch(() => null);
    const e = json?.error ?? {};
    throw new ApiError(e.code ?? 'HTTP', e.message ?? `HTTP ${res.status}`, res.status, e.fix, e.details);
  }
  if (!(res.headers.get('content-type') ?? '').includes('text/event-stream') || !res.body) return res.json();
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  let last: any = null;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let i: number;
    while ((i = buf.indexOf('\n\n')) >= 0) {
      const block = buf.slice(0, i); buf = buf.slice(i + 2);
      if (!/^(event|data):/m.test(block)) continue; // ": ping" keep-alives
      const event = block.match(/^event: (.+)$/m)?.[1] ?? 'message';
      const raw = block.match(/^data: (.*)$/m)?.[1];
      const data = raw ? JSON.parse(raw) : null;
      if (event === 'done') last = data;
      onEvent(event, data);
    }
  }
  return last;
}
